import { useState, useEffect, useRef } from "react";
import { useSEO } from "@/hooks/useSEO";
import { Phone, CheckCircle, ArrowRight, Shield, Clock, Award, TreeDeciduous, Upload, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileCTA from "@/components/MobileCTA";
import StickyCtaBar from "@/components/StickyCtaBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TreeRemovalPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service_type: "tree-removal",
    tree_count: 1,
    tree_height: "",
    urgency: "standard",
    include_stump_grinding: false,
    message: "",
  });
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => 
      ['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(file.type)
    );
    
    if (validFiles.length + images.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }
    
    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setImages([...images, ...newImages]);
  };

  const removeImage = (index) => {
    const newImages = [...images];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('address', formData.address);
      submitData.append('service_type', formData.service_type);
      submitData.append('tree_count', formData.tree_count.toString());
      submitData.append('tree_height', formData.tree_height);
      submitData.append('urgency', formData.urgency);
      submitData.append('include_stump_grinding', formData.include_stump_grinding.toString());
      submitData.append('message', formData.message);
      
      images.forEach((img) => {
        submitData.append('images', img.file);
      });

      const response = await fetch(`${API}/tree-removal-quotes`, {
        method: 'POST',
        body: submitData,
      });

      if (!response.ok) throw new Error('Failed to submit');
      
      toast.success("Tree removal quote submitted! Oro Valley Tree Service will contact you within 24 hours.");
      
      // Clear form
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        service_type: "tree-removal",
        tree_count: 1,
        tree_height: "",
        urgency: "standard",
        include_stump_grinding: false,
        message: "",
      });
      setImages([]);
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useSEO({
    title: "Tree Removal Tucson AZ | Tree Removal and Stump Grinding | Tree Removal Service",
    canonical: "https://tucsonstumpgrinding.com/tree-removal",
    description: "Professional tree removal service in Tucson AZ with stump grinding included. Licensed and insured tree removal and stump grinding for Oro Valley, Catalina, Sahuarita, SaddleBrooke and Oracle. Free estimates for tree stump removal.",
    ogTitle: "Tree Removal Tucson AZ | Tree Removal and Stump Grinding Service",
    ogDescription: "Professional tree removal service in Tucson AZ with stump grinding included. Licensed and insured. Free estimates.",
    ogUrl: "https://tucsonstumpgrinding.com/tree-removal",
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-20 md:pt-24 pb-16 lg:pb-24 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.pexels.com/photos/1179229/pexels-photo-1179229.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/95 via-[#1A1A1A]/85 to-[#1A1A1A]/70" />
          <div className="absolute inset-0 noise-bg" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Content */}
              <div className="text-white">
                <div className="w-16 h-1 bg-green-500 mb-6" />
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6 leading-tight uppercase">
                  Tree Removal<br />
                  <span className="text-green-500">Quote Request</span>
                </h1>
                <p className="font-body text-lg sm:text-xl text-gray-300 mb-6">
                  Need a tree removed? Our partner <span className="text-green-500 font-semibold">Oro Valley Tree Service</span> provides professional tree removal throughout Tucson and surrounding areas.
                </p>

                {/* Trust Badges */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {[
                    { icon: Shield, text: "Licensed & Insured" },
                    { icon: Award, text: "Certified Arborists" },
                    { icon: TreeDeciduous, text: "Full Service" },
                  ].map((badge, index) => (
                    <div key={index} className="flex items-center gap-2 px-3 py-2 bg-white/10 border border-white/20 rounded-sm">
                      <badge.icon className="w-4 h-4 text-green-500" />
                      <span className="font-body text-sm font-medium">{badge.text}</span>
                    </div>
                  ))}
                </div>

                {/* Benefits */}
                <ul className="space-y-3 mb-6">
                  {[
                    "Complete tree removal services",
                    "Same-day service with stump grinding*",
                    "Emergency tree removal available",
                    "Bundle with stump grinding & save",
                  ].map((benefit, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="font-body text-gray-200">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <p className="font-body text-xs text-gray-400 mb-6">
                  * Same-day stump grinding available when scheduled with tree removal service.
                </p>

                {/* Oro Valley Tree Service Link */}
                <div className="bg-green-500/20 border-2 border-green-500 p-4 rounded-sm">
                  <p className="font-body text-sm text-gray-300 mb-3">
                    Tree removal services provided by:
                  </p>
                  <a 
                    href="https://orovalleytreeservice.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 font-heading font-bold uppercase text-sm tracking-wide transition-colors"
                  >
                    <TreeDeciduous className="w-4 h-4" />
                    Oro Valley Tree Service
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Right - Quote Form */}
              <div className="bg-white border-3 border-[#1A1A1A] p-6 sm:p-8" data-testid="tree-removal-form">
                <div className="text-center mb-6">
                  <TreeDeciduous className="w-12 h-12 text-green-600 mx-auto mb-3" />
                  <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] uppercase">
                    Tree Removal Quote
                  </h2>
                  <p className="font-body text-gray-600 mt-2 text-sm">
                    Services provided by{" "}
                    <a 
                      href="https://orovalleytreeservice.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline font-semibold"
                    >
                      Oro Valley Tree Service
                    </a>
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="font-body font-semibold text-sm">Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 rounded-none"
                        placeholder="Your name"
                        data-testid="tree-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="font-body font-semibold text-sm">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-1 rounded-none"
                        placeholder="(520) 555-1234"
                        data-testid="tree-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="font-body font-semibold text-sm">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 rounded-none"
                      placeholder="your@email.com"
                      data-testid="tree-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address" className="font-body font-semibold text-sm">Property Address *</Label>
                    <Input
                      id="address"
                      type="text"
                      required
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="mt-1 rounded-none"
                      placeholder="123 Main St, Tucson, AZ"
                      data-testid="tree-address-input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-body font-semibold text-sm">Service Type *</Label>
                      <Select
                        value={formData.service_type}
                        onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                      >
                        <SelectTrigger className="mt-1 rounded-none" data-testid="tree-service-select">
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tree-removal">Tree Removal</SelectItem>
                          <SelectItem value="tree-trimming">Tree Trimming</SelectItem>
                          <SelectItem value="emergency">Emergency Service</SelectItem>
                          <SelectItem value="land-clearing">Land Clearing</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-body font-semibold text-sm">Number of Trees</Label>
                      <Select
                        value={formData.tree_count.toString()}
                        onValueChange={(value) => setFormData({ ...formData, tree_count: parseInt(value) })}
                      >
                        <SelectTrigger className="mt-1 rounded-none" data-testid="tree-count-select">
                          <SelectValue placeholder="Select count" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 Tree</SelectItem>
                          <SelectItem value="2">2 Trees</SelectItem>
                          <SelectItem value="3">3 Trees</SelectItem>
                          <SelectItem value="4">4 Trees</SelectItem>
                          <SelectItem value="5">5+ Trees</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-body font-semibold text-sm">Approximate Tree Height</Label>
                      <Select
                        value={formData.tree_height}
                        onValueChange={(value) => setFormData({ ...formData, tree_height: value })}
                      >
                        <SelectTrigger className="mt-1 rounded-none" data-testid="tree-height-select">
                          <SelectValue placeholder="Select height" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="small">Under 15 feet</SelectItem>
                          <SelectItem value="medium">15-30 feet</SelectItem>
                          <SelectItem value="large">30-50 feet</SelectItem>
                          <SelectItem value="extra-large">Over 50 feet</SelectItem>
                          <SelectItem value="unknown">Not Sure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-body font-semibold text-sm">Urgency</Label>
                      <Select
                        value={formData.urgency}
                        onValueChange={(value) => setFormData({ ...formData, urgency: value })}
                      >
                        <SelectTrigger className="mt-1 rounded-none" data-testid="tree-urgency-select">
                          <SelectValue placeholder="Select urgency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard (1-2 weeks)</SelectItem>
                          <SelectItem value="soon">Soon (within 1 week)</SelectItem>
                          <SelectItem value="urgent">Urgent (within 48 hours)</SelectItem>
                          <SelectItem value="emergency">Emergency (same day)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Include Stump Grinding Checkbox */}
                  <div className="flex items-center space-x-3 p-4 bg-[#FF4D00]/10 border border-[#FF4D00] rounded-sm">
                    <Checkbox
                      id="include_stump"
                      checked={formData.include_stump_grinding}
                      onCheckedChange={(checked) => setFormData({ ...formData, include_stump_grinding: checked })}
                      data-testid="include-stump-checkbox"
                    />
                    <div>
                      <Label htmlFor="include_stump" className="font-body font-semibold text-sm cursor-pointer">
                        Include Stump Grinding
                      </Label>
                      <p className="font-body text-xs text-gray-600">
                        Bundle stump grinding for same-day service & savings
                      </p>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <Label className="font-body font-semibold text-sm">
                      Upload Tree Photos (Optional)
                    </Label>
                    <p className="font-body text-xs text-gray-500 mb-2">
                      Photos help us provide a more accurate quote. Max 5 images.
                    </p>
                    <div 
                      className="mt-1 border-2 border-dashed border-gray-300 rounded-sm p-4 text-center cursor-pointer hover:border-green-500 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="font-body text-sm text-gray-600">
                        Click to upload photos
                      </p>
                      <p className="font-body text-xs text-gray-400">
                        JPEG, PNG, WebP (max 5 images)
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic"
                      multiple
                      className="hidden"
                      onChange={handleImageUpload}
                      data-testid="tree-image-upload"
                    />
                    
                    {/* Image Previews */}
                    {images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {images.map((img, index) => (
                          <div key={index} className="relative">
                            <img 
                              src={img.preview} 
                              alt={`Preview ${index + 1}`}
                              className="w-16 h-16 object-cover rounded border"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-body font-semibold text-sm">Additional Details</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="mt-1 rounded-none"
                      placeholder="Tell us about the tree(s), any concerns, access issues, etc..."
                      rows={3}
                      data-testid="tree-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-none font-heading font-bold uppercase tracking-wide"
                    data-testid="tree-submit-btn"
                  >
                    {isSubmitting ? "Submitting..." : "Request Tree Removal Quote"}
                  </Button>

                  <p className="text-center text-xs text-gray-500 font-body">
                    Services provided by{" "}
                    <a 
                      href="https://orovalleytreeservice.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline"
                    >
                      Oro Valley Tree Service
                    </a>
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Why Bundle Section */}
        <section className="py-16 lg:py-24 bg-[#F5F5F0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-[#FF4D00] mx-auto mb-4" />
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1A1A] uppercase mb-4">
                Bundle Tree Removal + Stump Grinding
              </h2>
              <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
                Get the complete job done in one visit. When you bundle tree removal with stump grinding, you save time and money.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Clock, title: "Same-Day Service", desc: "Tree removed and stump ground in a single visit" },
                { icon: Shield, title: "One Contractor", desc: "No need to coordinate multiple service providers" },
                { icon: Award, title: "Bundle Savings", desc: "Save money when you combine services" },
              ].map((item, index) => (
                <div key={index} className="bg-white border-2 border-[#E5E5E0] p-6 text-center">
                  <div className="w-12 h-12 bg-[#FF4D00] flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-[#1A1A1A] uppercase mb-2">
                    {item.title}
                  </h3>
                  <p className="font-body text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#1A1A1A] relative overflow-hidden noise-bg">
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase mb-4">
              Need Just Stump Grinding?
            </h2>
            <p className="font-body text-gray-400 text-lg mb-8">
              Already had your tree removed? We specialize in stump grinding services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="btn-primary px-8 py-4 text-lg rounded-none"
              >
                <a href="/#quote">Get Stump Grinding Quote</a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1A1A1A] px-8 py-4 text-lg rounded-none font-heading font-bold uppercase tracking-wide"
              >
                <a href="tel:+15202007739,2">(520) 200-7739 Ext 2</a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <MobileCTA />
      <StickyCtaBar />
    </div>
  );
};

export default TreeRemovalPage;
